import AWS, { AWSError } from "aws-sdk";
import { InvocationResponse } from "aws-sdk/clients/lambda";
import type { PromiseResult } from "aws-sdk/lib/request";
import {
  AppointmentType,
  AttorneyInfo,
  BodyPart,
  WorkersCompInsuranceInfo,
} from "~/api/API";
import { SupportedHoiGenAppointmentType } from "~/common/types";
import { getCurrentEnv } from "~/common/utils";
import { getConfig } from "~/config/configProvider";
import {
  AWSAuthError,
  AWSRequestAbortedError,
  ClockSkewError,
  LambdaError,
  NetworkError,
} from "~/error-handling";
import { isClockSkewError } from "~/error-handling/serviceErrorUtil";
import { WrappedServiceModule } from "~/hooks/useServices";
import {
  FormatAddressEvent,
  FormatAddressResponse,
} from "~/pydantic_models/lambdas/models/address_formatter";
import {
  AppointmentPdfParsingEvent,
  CreateAppointmentDocsEvent,
} from "~/pydantic_models/lambdas/models/appointment_operations";
import {
  AssessmentDictationPopulateEvent,
  AssessmentReviewGenEvent,
  AssessmentReviewGenResponse,
} from "~/pydantic_models/lambdas/models/assessment";
import { BackfillOnsiteOpsResponse } from "~/pydantic_models/lambdas/models/backfill";
import {
  GenerateCoverLetterRecapEvent,
  GenerateCoverLetterRecapResponse,
} from "~/pydantic_models/lambdas/models/cover_letter_recap";
import {
  DifficultyLevelEvent,
  DifficultyLevelResponse,
} from "~/pydantic_models/lambdas/models/difficulty_level";
import {
  GenerateBillingFormEvent,
  GenerateBillingFormResponse,
  GenerateDiagnosticStudyPrescriptionEvent,
  GenerateDiagnosticStudyPrescriptionResponse,
  GenerateDiagnosticTestEvent,
  GenerateDiagnosticTestResponse,
  GenerateNoticeLetterEvent,
  GenerateNoticeLetterResponse,
  GenerateNotificationFormEvent,
  GenerateNotificationFormResponse,
  GenerateRtwVoucherFormEvent,
  GenerateRtwVoucherFormResponse,
} from "~/pydantic_models/lambdas/models/documents";
import {
  CreateTestAppointmentEvent,
  CreateTestAppointmentResponse,
} from "~/pydantic_models/lambdas/models/dummy_appointment";
import {
  GenerateAttendanceEmailEvent,
  GenerateConfirmationEmailEvent,
  GenerateEmailResponse,
  GeneratePreVisitCallSchedulingEmailEvent,
  GenerateRecordsFollowupEmailEvent,
} from "~/pydantic_models/lambdas/models/emails";
import {
  GenerateAllEvent,
  GenerateAllResponse,
} from "~/pydantic_models/lambdas/models/generate_all";
import {
  DuplicateGoogleDocEvent,
  DuplicateGoogleDocResponse,
  FormatMode,
  GenerateDiagnosticStudyPrescriptionGoogleDocResponse,
  GenerateDiagnosticTestFormGoogleDocResponse,
  GenerateGoogleDocLinkEvent,
  GenerateGoogleDocLinkResponse,
  GenerateRecordBriefingGoogleDocLinkEvent,
  GenerateRecordBriefingGoogleDocLinkResponse,
  GetUnresolvedCommentsCountEvent,
  GetUnresolvedCommentsCountResponse,
} from "~/pydantic_models/lambdas/models/google_doc";
import {
  HistoryMrrCrossValidationEvent,
  HistoryMrrCrossValidationResponse,
} from "~/pydantic_models/lambdas/models/history_mrr_cross_validation";
import {
  HistoryData,
  HistoryValidationEvent,
  HistoryValidationResponse,
} from "~/pydantic_models/lambdas/models/history_validation";
import {
  HoiAppendBodyPartsRequest,
  HoiAppendBodyPartsResponse,
} from "~/pydantic_models/lambdas/models/hoi";
import {
  AdlSections,
  DynamicHoiGenEvent,
  DynamicHoiGenResponse,
  MedicalHistoryFields,
} from "~/pydantic_models/lambdas/models/hoi_dynamic";
import {
  AppointmentType as HoiGenAppointmentType,
  HoiGenEmployerInfo,
  HoiGenEmploymentGapInfo,
  HoiGenPatientInfo,
} from "~/pydantic_models/lambdas/models/hoi_gen";
import {
  HoiInsightsGenEvent,
  HoiInsightsGenResponse,
} from "~/pydantic_models/lambdas/models/hoi_insights_gen";
import {
  ImpairmentRatingEvent,
  ImpairmentRatingPatientInfo,
} from "~/pydantic_models/lambdas/models/impairment_rating";
import {
  EndImpersonationResponse,
  InitiateImpersonationResponse,
} from "~/pydantic_models/lambdas/models/impersonation";
import {
  GenerateIntroParagraphEvent,
  GenerateIntroParagraphResponse,
} from "~/pydantic_models/lambdas/models/intro_paragraph";
import {
  ConsolidatePartyEvent,
  ConsolidatePartyResponse,
} from "~/pydantic_models/lambdas/models/intro_section";
import {
  GenerateJobDutiesEvent,
  GenerateJobDutiesResponse,
} from "~/pydantic_models/lambdas/models/job_duties_gen";
import {
  GetJotformSubmissionByAppointmentEvent,
  GetJotformSubmissionByIdEvent,
  GetJotformSubmissionResponse,
  JotformFormType,
  Language,
} from "~/pydantic_models/lambdas/models/jotform";
import { MedicalRecordValidationEvent } from "~/pydantic_models/lambdas/models/medical_record_validation";
import {
  CreateEmptyMedicalRecordEvent,
  CreateEmptyMedicalRecordResponse,
} from "~/pydantic_models/lambdas/models/medical_records";
import {
  CorrectMedicationsEvent,
  CorrectMedicationsResponse,
} from "~/pydantic_models/lambdas/models/medication_correction";
import {
  PastInjuryMatrixGenEvent,
  PastInjuryMatrixGenResponse,
} from "~/pydantic_models/lambdas/models/past_injury_matrix_gen";
import {
  GeneratePDFPackageRequest,
  GeneratePDFPackageResponse,
} from "~/pydantic_models/lambdas/models/pdf_package";
import {
  PhysicalExamInsightsGenEvent,
  PhysicalExamInsightsGenResponse,
} from "~/pydantic_models/lambdas/models/physical_exam_insights_gen";
import {
  PhysicalExamPreviewEvent,
  PhysicalExamPreviewResponse,
} from "~/pydantic_models/lambdas/models/physical_exam_preview";
import {
  GeneratePreHistoryTemplateEvent,
  GeneratePreHistoryTemplateResponse,
} from "~/pydantic_models/lambdas/models/pre_history_template_gen";
import {
  GenerateRecapEvent,
  GenerateRecapResponse,
  RecapSection,
  RecapType,
  SelectedSections,
} from "~/pydantic_models/lambdas/models/recap";
import {
  RecordBriefingInsightsGenEvent,
  RecordBriefingInsightsGenResponse,
} from "~/pydantic_models/lambdas/models/record_briefing_insights_gen";
import {
  ResummarizeSegmentEvent,
  ResummarizeSegmentInput,
  ResummarizeSegmentResponse,
  SummarizeSubRosaEntryEvent,
} from "~/pydantic_models/lambdas/models/workflow";
import {
  MergeUploadedFilesEvent,
  MergeUploadedFilesResponse,
  ResolveAttachmentReviewEvent,
  ResolveAttachmentReviewResponse,
} from "~/pydantic_models/lambdas/models/workflow_audit_actions";
import {
  AppointmentEventAction,
  AppointmentEventEntity,
} from "~/pydantic_models/services/models/events/appointment_event";
import {
  MergeAllSourcesEvent,
  MergeAllSourcesResponse,
  MergeFieldInput,
} from "~/pydantic_models/services/models/llm/history_merge";
import {
  HistoryTranscriptInputData,
  HistoryTranscriptPopulationEvent,
  HistoryTranscriptPopulationResponse,
  PatientDemographicsData,
} from "~/pydantic_models/services/models/llm/history_transcript";
import { AppointmentPdfParsingResponse } from "~/pydantic_models/services/models/llm/pdf_parser";
import { enqueueAppointmentEventViaECS } from "~/services/appointmentEvents";
import * as s3Service from "~/services/s3";

export const RECAP_SECTION_KEYS: Record<RecapSection, RecapSection> = {
  CAUSATION: "CAUSATION",
  DIAGNOSIS: "DIAGNOSIS",
  MMI_FUTURE_MEDICAL_TREATMENT: "MMI_FUTURE_MEDICAL_TREATMENT",
  IMPAIRMENT_RATING: "IMPAIRMENT_RATING",
  APPORTIONMENT: "APPORTIONMENT",
  WORK_STATUS: "WORK_STATUS",
};

const config = getConfig();

// Lambda functions with provisioned concurrency per environment.
// Only prod has provisioned concurrency; dev/stage invoke $LATEST directly.
const ALIASED_LAMBDA_FUNCTIONS: Record<string, Set<string>> = {
  dev: new Set(["VectorSearchDebugFunction"]),
  stage: new Set([]),
  prod: new Set([
    "CreateAppointmentDocsFunction",
    "PdfParserFunction",
    "ResummarizeSegmentFunction",
    "VectorSearchDebugFunction",
    "GenerateHistoryValidationFunction",
    "GetUnresolvedCommentsCountFunction",
    "AssessmentDictationPopulateFunction",
  ]),
};

/**
 * Returns the function name with alias qualifier if the function has provisioned concurrency
 * in the current environment. Functions with provisioned concurrency need to be invoked via
 * their alias to benefit from warm starts.
 */
const getFunctionNameWithAlias = (functionName: string): string => {
  const env = getCurrentEnv();
  const aliasedFunctions = ALIASED_LAMBDA_FUNCTIONS[env] ?? new Set();
  // Extract the base function name (e.g., "IndigoLambdaStack-dev-PdfParserFunction" -> "PdfParserFunction")
  const baseName = functionName.split("-").pop() || functionName;

  if (aliasedFunctions.has(baseName)) {
    return `${functionName}:live`;
  }
  return functionName;
};

/**
 * Gets a Google Doc link for a given file ID by invoking the records doc file lambda.
 * This is a generic function that replaces the duplicate functions for different document types.
 *
 * @param lambdaClient AWS Lambda client
 * @param fileID The file ID to get the Google Doc link for
 * @param duration The duration for the link
 * @returns GenerateGoogleDocLinkResponse with the Google Doc link
 */
export const getGoogleDocLinkFile = async (
  lambdaClient: AWS.Lambda,
  fileID: string,
  duration: number,
) => {
  const payload_data: GenerateGoogleDocLinkEvent = {
    file_id: fileID,
    duration: duration,
  };
  const params = {
    FunctionName: config.RECORDS_DOC_FILE_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(error, config.RECORDS_DOC_FILE_LAMBDA_FUNCTION);
  }

  return deserializeLambdaPayload<GenerateGoogleDocLinkResponse>(
    res,
    config.RECORDS_DOC_FILE_LAMBDA_FUNCTION,
  );
};

export const getUnresolvedCommentsCount = async (
  lambdaClient: AWS.Lambda,
  fileId: string,
) => {
  const payload_data: GetUnresolvedCommentsCountEvent = {
    file_id: fileId,
  };
  const params = {
    FunctionName: getFunctionNameWithAlias(
      config.GET_UNRESOLVED_COMMENTS_COUNT_LAMBDA_FUNCTION,
    ),
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.GET_UNRESOLVED_COMMENTS_COUNT_LAMBDA_FUNCTION,
    );
  }

  return deserializeLambdaPayload<GetUnresolvedCommentsCountResponse>(
    res,
    config.GET_UNRESOLVED_COMMENTS_COUNT_LAMBDA_FUNCTION,
  );
};

export const duplicateGoogleDocs = async (
  lambdaClient: AWS.Lambda,
  sourceFileIds: string[],
  appointmentRecordId: string,
) => {
  const payload_data: DuplicateGoogleDocEvent = {
    source_file_ids: sourceFileIds,
    appointment_record_id: appointmentRecordId,
  };
  const params = {
    FunctionName: config.DUPLICATE_GOOGLE_DOC_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(error, config.DUPLICATE_GOOGLE_DOC_LAMBDA_FUNCTION);
  }

  return deserializeLambdaPayload<DuplicateGoogleDocResponse>(
    res,
    config.DUPLICATE_GOOGLE_DOC_LAMBDA_FUNCTION,
  );
};

export const createAppointmentDocs = async (
  lambdaClient: AWS.Lambda,
  appointmentID: string,
  bodyParts: BodyPart[],
  intakeFormS3Keys: string[] = [],
) => {
  const payload_data: CreateAppointmentDocsEvent = {
    appointment_record_id: appointmentID,
    body_parts: bodyParts,
    intake_form_s3_keys: intakeFormS3Keys,
  };
  const params = {
    FunctionName: getFunctionNameWithAlias(
      config.RECORDS_CREATE_APPOINTMENT_DOCS_LAMBDA_FUNCTION,
    ),
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.RECORDS_CREATE_APPOINTMENT_DOCS_LAMBDA_FUNCTION,
    );
  }

  return deserializeLambdaPayload<void>(
    res,
    config.RECORDS_CREATE_APPOINTMENT_DOCS_LAMBDA_FUNCTION,
  );
};

export const resummarizeSegment = async (
  lambdaClient: AWS.Lambda,
  appointmentID: string,
  documentID: string,
  segments: ResummarizeSegmentInput[],
) => {
  const payload_data: ResummarizeSegmentEvent = {
    appointment_record_id: appointmentID,
    document_id: documentID,
    segments,
  };
  const params = {
    FunctionName: getFunctionNameWithAlias(
      config.RECORDS_RESUMMARIZE_SEGMENT_LAMBDA_FUNCTION,
    ),
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.RECORDS_RESUMMARIZE_SEGMENT_LAMBDA_FUNCTION,
    );
  }

  return deserializeLambdaPayload<ResummarizeSegmentResponse>(
    res,
    config.RECORDS_RESUMMARIZE_SEGMENT_LAMBDA_FUNCTION,
  );
};

/** Summarize a sub-rosa entry's HH:MM:SS window of its video into the
 * entry's draft. Same response shape as resummarizeSegment (one result), so
 * callers handle both alike. */
export const summarizeSubRosaEntry = async (
  lambdaClient: AWS.Lambda,
  appointmentID: string,
  documentID: string,
  logicalSegmentID: string,
  videoStartTime?: string,
  videoEndTime?: string,
) => {
  const payload_data: SummarizeSubRosaEntryEvent = {
    appointment_record_id: appointmentID,
    document_id: documentID,
    logical_segment_id: logicalSegmentID,
    ...(videoStartTime && { video_start_time: videoStartTime }),
    ...(videoEndTime && { video_end_time: videoEndTime }),
  };
  const params = {
    FunctionName: getFunctionNameWithAlias(
      config.RECORDS_SUMMARIZE_SUB_ROSA_ENTRY_LAMBDA_FUNCTION,
    ),
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.RECORDS_SUMMARIZE_SUB_ROSA_ENTRY_LAMBDA_FUNCTION,
    );
  }

  return deserializeLambdaPayload<ResummarizeSegmentResponse>(
    res,
    config.RECORDS_SUMMARIZE_SUB_ROSA_ENTRY_LAMBDA_FUNCTION,
  );
};

export interface TriageV2Response {
  answer?: string;
  correlation_id?: string;
  audit_id?: string;
  conversation_id?: string;
  error?: string;
}

/**
 * Run Triage V2 (agent-orchestrated) for a ticket — a fresh pass, or a resume
 * of the latest run with ``message`` (resume=true). Synchronous + long-running
 * (the orchestrator takes minutes), so it's registered in useServices'
 * LONG_RUNNING_FUNCTIONS for the extended socket timeout.
 */
export const runTriageV2 = async (
  lambdaClient: AWS.Lambda,
  ticketId: number,
  message: string,
  resume = true,
  workingMemory = true,
  // For a resume: the exact audit + OpenAI conversation the portal is showing,
  // so the lambda continues that run rather than re-resolving "the latest".
  conversationId?: string,
  auditId?: string,
) => {
  const params = {
    FunctionName: getFunctionNameWithAlias(config.TRIAGE_V2_LAMBDA_FUNCTION),
    Payload: JSON.stringify({
      ticket_id: ticketId,
      message,
      resume,
      working_memory: workingMemory,
      ...(conversationId ? { conversation_id: conversationId } : {}),
      ...(auditId ? { audit_id: auditId } : {}),
    }),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(error, config.TRIAGE_V2_LAMBDA_FUNCTION);
  }

  return deserializeLambdaPayload<TriageV2Response>(
    res,
    config.TRIAGE_V2_LAMBDA_FUNCTION,
  );
};

export const createEmptyMedicalRecord = async (
  lambdaClient: AWS.Lambda,
  appointmentID: string,
) => {
  const payload_data: CreateEmptyMedicalRecordEvent = {
    appointment_record_id: appointmentID,
  };
  const params = {
    FunctionName: config.RECORDS_CREATE_EMPTY_MEDICAL_RECORD_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.RECORDS_CREATE_EMPTY_MEDICAL_RECORD_LAMBDA_FUNCTION,
    );
  }

  return deserializeLambdaPayload<CreateEmptyMedicalRecordResponse>(
    res,
    config.RECORDS_CREATE_EMPTY_MEDICAL_RECORD_LAMBDA_FUNCTION,
  );
};

export const getBillingPDF = async (
  lambdaClient: AWS.Lambda,
  data: GenerateBillingFormEvent,
) => {
  const params = {
    FunctionName: config.BILLING_PDF_GENERATION_LAMBDA_FUNCTION,
    Payload: JSON.stringify(data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.BILLING_PDF_GENERATION_LAMBDA_FUNCTION,
    );
  }
  const payload = deserializeLambdaPayload<GenerateBillingFormResponse>(
    res,
    config.BILLING_PDF_GENERATION_LAMBDA_FUNCTION,
  );
  return payload.body.billing_form_pdf;
};

export const draftAttendanceEmail = async (
  lambdaClient: AWS.Lambda,
  data: GenerateAttendanceEmailEvent,
): Promise<GenerateEmailResponse> => {
  const params = {
    FunctionName: config.EMAIL_GENERATION_LAMBDA_FUNCTION,
    Payload: JSON.stringify(data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(error, config.EMAIL_GENERATION_LAMBDA_FUNCTION);
  }
  const payload = deserializeLambdaPayload<GenerateEmailResponse>(
    res,
    config.EMAIL_GENERATION_LAMBDA_FUNCTION,
  );
  return payload.body;
};

export const draftConfirmationEmail = async (
  lambdaClient: AWS.Lambda,
  data: GenerateConfirmationEmailEvent,
): Promise<GenerateEmailResponse> => {
  const params = {
    FunctionName: config.EMAIL_GENERATION_LAMBDA_FUNCTION,
    Payload: JSON.stringify(data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(error, config.EMAIL_GENERATION_LAMBDA_FUNCTION);
  }
  const payload = deserializeLambdaPayload<GenerateEmailResponse>(
    res,
    config.EMAIL_GENERATION_LAMBDA_FUNCTION,
  );
  return payload.body;
};

export const draftRecordsFollowUpEmail = async (
  lambdaClient: AWS.Lambda,
  data: GenerateRecordsFollowupEmailEvent,
): Promise<GenerateEmailResponse> => {
  const params = {
    FunctionName: config.EMAIL_GENERATION_LAMBDA_FUNCTION,
    Payload: JSON.stringify(data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(error, config.EMAIL_GENERATION_LAMBDA_FUNCTION);
  }
  const payload = deserializeLambdaPayload<GenerateEmailResponse>(
    res,
    config.EMAIL_GENERATION_LAMBDA_FUNCTION,
  );
  return payload.body;
};

export const draftPreVisitCallSchedulingEmail = async (
  lambdaClient: AWS.Lambda,
  data: GeneratePreVisitCallSchedulingEmailEvent,
): Promise<GenerateEmailResponse> => {
  const params = {
    FunctionName: config.EMAIL_GENERATION_LAMBDA_FUNCTION,
    Payload: JSON.stringify(data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(error, config.EMAIL_GENERATION_LAMBDA_FUNCTION);
  }
  const payload = deserializeLambdaPayload<GenerateEmailResponse>(
    res,
    config.EMAIL_GENERATION_LAMBDA_FUNCTION,
  );
  return payload.body;
};

export const parseNewAppointmentIntakeForms = async (
  lambdaClient: AWS.Lambda,
  s3ServiceParam: WrappedServiceModule<typeof s3Service>,
  panelFiles: File[] = [],
  demographicFiles: File[] = [],
  fileType: string,
  options?: {
    onProgress?: (progress: { loaded: number; total?: number }) => void;
    onS3KeysUploaded?: (s3Keys: string[]) => void;
  },
) => {
  // Upload files to S3 and collect keys
  const panelKeys: string[] = [];
  const demographicKeys: string[] = [];

  // UUID for this upload session as s3 folder to contain the files
  const uuid = crypto.randomUUID();

  // Upload panel files
  for (const file of panelFiles) {
    const uniqueKey = `temp-pdfs/${uuid}/${file.name}`;
    const uploadResult = await s3ServiceParam.uploadFile(
      config.TEMP_DOCUMENTS_BUCKET_NAME,
      uniqueKey,
      file,
      {
        onProgress: options?.onProgress,
      },
    );

    if (uploadResult.error) {
      throw new Error(
        `Failed to upload panel file ${file.name}: ${uploadResult.error.message}`,
      );
    }

    panelKeys.push(uniqueKey);
  }

  // Upload demographic files
  for (const file of demographicFiles) {
    const uniqueKey = `temp-pdfs/${uuid}/${file.name}`;
    const uploadResult = await s3ServiceParam.uploadFile(
      config.TEMP_DOCUMENTS_BUCKET_NAME,
      uniqueKey,
      file,
      {
        onProgress: options?.onProgress,
      },
    );

    if (uploadResult.error) {
      throw new Error(
        `Failed to upload demographic file ${file.name}: ${uploadResult.error.message}`,
      );
    }

    demographicKeys.push(uniqueKey);
  }

  // Notify caller about uploaded S3 keys - consolidate into single list
  if (options?.onS3KeysUploaded) {
    options.onS3KeysUploaded([...panelKeys, ...demographicKeys]);
  }

  const payload_data: AppointmentPdfParsingEvent = {
    file_type: fileType,
    panel_files: panelKeys,
    demographic_files: demographicKeys,
  };
  const params = {
    FunctionName: getFunctionNameWithAlias(
      config.RECORDS_APPOINTMENT_PDF_PARSER_LAMBDA_FUNCTION,
    ),
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.RECORDS_APPOINTMENT_PDF_PARSER_LAMBDA_FUNCTION,
    );
  }
  return deserializeLambdaPayload<AppointmentPdfParsingResponse>(
    res,
    config.RECORDS_APPOINTMENT_PDF_PARSER_LAMBDA_FUNCTION,
  );
};

const mapHoiGenSupportedAppointmentTypeToPydantic = (
  type: SupportedHoiGenAppointmentType,
): HoiGenAppointmentType => {
  switch (type) {
    case AppointmentType.QME:
      return "QME";
    case AppointmentType.AME:
      return "AME";
    case AppointmentType.QME_REEVALUATION:
      return "QME_REEVALUATION";
    case AppointmentType.AME_REEVALUATION:
      return "AME_REEVALUATION";
    case AppointmentType.SIBTF:
      return "SIBTF";
    case AppointmentType.SIBTF_REEVALUATION:
      return "SIBTF_REEVALUATION";
    case AppointmentType.IME:
      return "IME";
    case AppointmentType.IME_REEVALUATION:
      return "IME_REEVALUATION";
    case AppointmentType.IME_ADR:
      return "IME_ADR";
    case AppointmentType.IME_ADR_REEVALUATION:
      return "IME_ADR_REEVALUATION";
  }
};

export const generateHistoryOfInjuriesDynamic = async (
  lambdaClient: AWS.Lambda,
  appointmentID: string,
  appointmentType: SupportedHoiGenAppointmentType,
  appointmentDateISO: string,
  claimNumber: string,
  patientInfo: HoiGenPatientInfo,
  employerAtInjuryTime: HoiGenEmployerInfo,
  employersPostInjury: HoiGenEmployerInfo[],
  previousEmployers: HoiGenEmployerInfo[],
  employmentGaps: HoiGenEmploymentGapInfo[],
  medicalHistoryFields: MedicalHistoryFields,
  adlSections: AdlSections,
  historyNotes: string,
  currentComplaintNotes: string,
) => {
  const payload_data: DynamicHoiGenEvent = {
    claim_number: claimNumber,
    appointment_record_id: appointmentID,
    appointment_type:
      mapHoiGenSupportedAppointmentTypeToPydantic(appointmentType),
    appointment_date: appointmentDateISO,
    s3_bucket_name: config.RECORDS_BUCKET_NAME,
    patient_info: patientInfo,
    employer_at_injury_time: employerAtInjuryTime,
    employers_post_injury: employersPostInjury,
    previous_employers: previousEmployers,
    employment_gaps: employmentGaps,
    medical_history_fields: medicalHistoryFields,
    adl_sections: adlSections,
    history_notes: historyNotes,
    current_complaint_notes: currentComplaintNotes,
  };

  const params = {
    FunctionName: config.HOI_GENERATION_DYNAMIC_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.HOI_GENERATION_DYNAMIC_LAMBDA_FUNCTION,
    );
  }

  return deserializeLambdaPayload<DynamicHoiGenResponse>(
    res,
    config.HOI_GENERATION_DYNAMIC_LAMBDA_FUNCTION,
  );
};

export const generateHistoryOfInjuryInsights = async (
  lambdaClient: AWS.Lambda,
  appointmentID: string,
): Promise<HoiInsightsGenResponse> => {
  const payload_data: HoiInsightsGenEvent = {
    appointment_record_id: appointmentID,
  };

  const params = {
    FunctionName: config.GENERATE_HISTORY_OF_INJURY_INSIGHTS_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.GENERATE_HISTORY_OF_INJURY_INSIGHTS_LAMBDA_FUNCTION,
    );
  }
  const deserializedPayload = deserializeLambdaPayload<HoiInsightsGenResponse>(
    res,
    config.GENERATE_HISTORY_OF_INJURY_INSIGHTS_LAMBDA_FUNCTION,
  );
  return deserializedPayload.body;
};

export const generateRecordBriefingInsights = async (
  lambdaClient: AWS.Lambda,
  appointmentID: string,
): Promise<RecordBriefingInsightsGenResponse> => {
  const payload_data: RecordBriefingInsightsGenEvent = {
    appointment_record_id: appointmentID,
  };

  const params = {
    FunctionName: config.GENERATE_RECORD_BRIEFING_INSIGHTS_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.GENERATE_RECORD_BRIEFING_INSIGHTS_LAMBDA_FUNCTION,
    );
  }
  const deserializedPayload =
    deserializeLambdaPayload<RecordBriefingInsightsGenResponse>(
      res,
      config.GENERATE_RECORD_BRIEFING_INSIGHTS_LAMBDA_FUNCTION,
    );
  return deserializedPayload.body;
};

export const generatePhysicalExamInsights = async (
  lambdaClient: AWS.Lambda,
  appointmentID: string,
): Promise<PhysicalExamInsightsGenResponse> => {
  const payload_data: PhysicalExamInsightsGenEvent = {
    appointment_record_id: appointmentID,
  };

  const params = {
    FunctionName: config.GENERATE_PHYSICAL_EXAM_INSIGHTS_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.GENERATE_PHYSICAL_EXAM_INSIGHTS_LAMBDA_FUNCTION,
    );
  }
  const deserializedPayload =
    deserializeLambdaPayload<PhysicalExamInsightsGenResponse>(
      res,
      config.GENERATE_PHYSICAL_EXAM_INSIGHTS_LAMBDA_FUNCTION,
    );
  return deserializedPayload.body;
};

/**
 * Renders the appointment's native structured Physical Exam (exactly what the
 * Final Report will insert) to a .docx and returns a short-lived presigned URL
 * that downloads it, so the MA can review it before report generation. The
 * preview is transient — nothing is persisted; the report re-renders from
 * scratch. Rejects (400) when there's nothing to preview.
 */
export const generatePhysicalExamPreview = async (
  lambdaClient: AWS.Lambda,
  appointmentID: string,
): Promise<PhysicalExamPreviewResponse> => {
  const payload_data: PhysicalExamPreviewEvent = {
    appointment_record_id: appointmentID,
  };

  const params = {
    FunctionName: config.GENERATE_PHYSICAL_EXAM_PREVIEW_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.GENERATE_PHYSICAL_EXAM_PREVIEW_LAMBDA_FUNCTION,
    );
  }
  const deserializedPayload =
    deserializeLambdaPayload<PhysicalExamPreviewResponse>(
      res,
      config.GENERATE_PHYSICAL_EXAM_PREVIEW_LAMBDA_FUNCTION,
    );
  return deserializedPayload.body;
};

/**
 * Generates all insights (History of Injury, Record Briefing, and Physical Exam) in parallel.
 * This replaces the old generateCaseSummary function by calling the individual insights lambdas.
 */
export const generateAllInsights = async (
  lambdaClient: AWS.Lambda,
  appointmentID: string,
): Promise<{
  historyOfInjuryInsights: HoiInsightsGenResponse;
  recordBriefingInsights: RecordBriefingInsightsGenResponse;
  physicalExamInsights: PhysicalExamInsightsGenResponse;
}> => {
  const [
    historyOfInjuryInsights,
    recordBriefingInsights,
    physicalExamInsights,
  ] = await Promise.all([
    generateHistoryOfInjuryInsights(lambdaClient, appointmentID),
    generateRecordBriefingInsights(lambdaClient, appointmentID),
    generatePhysicalExamInsights(lambdaClient, appointmentID),
  ]);

  return {
    historyOfInjuryInsights,
    recordBriefingInsights,
    physicalExamInsights,
  };
};

export const generatePastInjuryMatrix = async (
  lambdaClient: AWS.Lambda,
  appointmentID: string,
): Promise<PastInjuryMatrixGenResponse> => {
  const payload_data: PastInjuryMatrixGenEvent = {
    appointment_record_id: appointmentID,
  };

  const params = {
    FunctionName: config.GENERATE_PAST_INJURY_MATRIX_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.GENERATE_PAST_INJURY_MATRIX_LAMBDA_FUNCTION,
    );
  }
  const deserializedPayload =
    deserializeLambdaPayload<PastInjuryMatrixGenResponse>(
      res,
      config.GENERATE_PAST_INJURY_MATRIX_LAMBDA_FUNCTION,
    );
  return deserializedPayload.body;
};

export const generateHistoryValidation = async (
  lambdaClient: AWS.Lambda,
  appointmentID: string,
  historyData: HistoryData,
): Promise<HistoryValidationResponse> => {
  const payload_data: HistoryValidationEvent = {
    appointment_record_id: appointmentID,
    history_data: historyData,
  };

  const params = {
    FunctionName: getFunctionNameWithAlias(
      config.GENERATE_HISTORY_VALIDATION_LAMBDA_FUNCTION,
    ),
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.GENERATE_HISTORY_VALIDATION_LAMBDA_FUNCTION,
    );
  }

  const payload = deserializeLambdaPayload<HistoryValidationResponse>(
    res,
    config.GENERATE_HISTORY_VALIDATION_LAMBDA_FUNCTION,
  );

  return payload.body;
};

export const populateHistoryTranscript = async (
  lambdaClient: AWS.Lambda,
  appointmentID: string,
  historyData: HistoryTranscriptInputData,
  demographics: PatientDemographicsData,
  additionalPrompt: string | null,
): Promise<HistoryTranscriptPopulationResponse> => {
  const payload_data: HistoryTranscriptPopulationEvent = {
    appointment_record_id: appointmentID,
    history_data: historyData,
    demographics,
    additional_prompt: additionalPrompt,
  };

  const params = {
    FunctionName: getFunctionNameWithAlias(
      config.GEN_HISTORY_TRANSCRIPT_POPULATE_LAMBDA_FUNCTION,
    ),
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.GEN_HISTORY_TRANSCRIPT_POPULATE_LAMBDA_FUNCTION,
    );
  }

  return deserializeLambdaPayload<HistoryTranscriptPopulationResponse>(
    res,
    config.GEN_HISTORY_TRANSCRIPT_POPULATE_LAMBDA_FUNCTION,
  ).body;
};

/**
 * History merge (Flow B): reconciles each already-populated note field into
 * one value. The caller sends every field's full raw text — source markers
 * inline — and gets back only the fields that materially changed; single-source
 * fields are omitted by the prompt. The lambda does no fetching of its own.
 */
export const mergePopulatedSources = async (
  lambdaClient: AWS.Lambda,
  appointmentID: string,
  fields: MergeFieldInput[],
): Promise<MergeAllSourcesResponse> => {
  const payload_data: MergeAllSourcesEvent = {
    appointment_record_id: appointmentID,
    fields,
  };

  const params = {
    FunctionName: getFunctionNameWithAlias(
      config.GEN_HISTORY_MERGE_POPULATED_SOURCES_LAMBDA_FUNCTION,
    ),
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.GEN_HISTORY_MERGE_POPULATED_SOURCES_LAMBDA_FUNCTION,
    );
  }

  return deserializeLambdaPayload<MergeAllSourcesResponse>(
    res,
    config.GEN_HISTORY_MERGE_POPULATED_SOURCES_LAMBDA_FUNCTION,
  ).body;
};

/**
 * Fire-and-forget the medical-record validation lambda. Uses InvocationType
 * "Event" so the call returns immediately with a 202 — the run can take
 * minutes and its status is tracked in OperationStatus, which the trigger
 * button watches (see useOperationStatusButton). The new Validation row is
 * linked on the appointment when the run completes; the caller re-reads it
 * then rather than from a response here.
 */
export const triggerMedicalRecordValidation = async (
  lambdaClient: AWS.Lambda,
  appointmentID: string,
): Promise<void> => {
  const payload_data: MedicalRecordValidationEvent = {
    appointment_record_id: appointmentID,
  };

  const params = {
    FunctionName: config.GENERATE_MEDICAL_RECORD_VALIDATION_LAMBDA_FUNCTION,
    InvocationType: "Event",
    Payload: JSON.stringify(payload_data),
  };

  try {
    await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.GENERATE_MEDICAL_RECORD_VALIDATION_LAMBDA_FUNCTION,
    );
  }
};

export const generateHistoryMrrCrossValidation = async (
  lambdaClient: AWS.Lambda,
  appointmentID: string,
): Promise<HistoryMrrCrossValidationResponse> => {
  const payload_data: HistoryMrrCrossValidationEvent = {
    appointment_record_id: appointmentID,
  };

  const params = {
    FunctionName: config.GENERATE_HISTORY_MRR_CROSS_VALIDATION_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.GENERATE_HISTORY_MRR_CROSS_VALIDATION_LAMBDA_FUNCTION,
    );
  }

  const payload = deserializeLambdaPayload<HistoryMrrCrossValidationResponse>(
    res,
    config.GENERATE_HISTORY_MRR_CROSS_VALIDATION_LAMBDA_FUNCTION,
  );

  return payload.body;
};

/**
 * Triggers impairment rating asynchronously (fire-and-forget). Uses
 * InvocationType "Event" so the call returns immediately with a 202 — the
 * portal watches OperationStatus for progress instead of awaiting the (multi-
 * minute) result. See useOperationStatusButton.
 */
export const triggerImpairmentRating = async (
  lambdaClient: AWS.Lambda,
  appointmentID: string,
  patientInfo: ImpairmentRatingPatientInfo,
  bodyParts: BodyPart[],
  enabledStrictRating: boolean,
  enabledAlmarazGuzman: boolean,
  enabledPainRating: boolean,
  enabledStrengthRating: boolean,
  enabledDBE: boolean,
  physicalExam: string,
  adl: string,
  currentComplaint: string,
  surgeries: string,
  diagnosticTests: string,
  diagnosis: string,
  autoMode: boolean = true,
  isDoctor: boolean = false,
): Promise<void> => {
  const payload_data: ImpairmentRatingEvent = {
    appointment_record_id: appointmentID,
    patient_info: patientInfo,
    body_parts: bodyParts,
    enabled_strict_rating: enabledStrictRating,
    enabled_almaraz_guzman: enabledAlmarazGuzman,
    enabled_pain_rating: enabledPainRating,
    enabled_strength_rating: enabledStrengthRating,
    enabled_dbe: enabledDBE,
    manual_input_physical_exam: physicalExam,
    manual_input_adl: adl,
    manual_input_current_complaint: currentComplaint,
    manual_input_surgeries: surgeries,
    manual_input_diagnostic_tests: diagnosticTests,
    manual_input_diagnosis: diagnosis,
    auto_mode: autoMode,
    is_doctor: isDoctor,
  };

  const params = {
    FunctionName: config.IMPAIRMENT_RATING_LAMBDA_FUNCTION,
    InvocationType: "Event",
    Payload: JSON.stringify(payload_data),
  };

  try {
    await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(error, config.IMPAIRMENT_RATING_LAMBDA_FUNCTION);
  }
};

export const generateRecordBriefingGoogleDocLink = async (
  lambdaClient: AWS.Lambda,
  appointmentId: string,
  formatMode?: FormatMode,
) => {
  const payload_data: GenerateRecordBriefingGoogleDocLinkEvent = {
    appointment_id: appointmentId,
    ...(formatMode && { format_mode: formatMode }),
  };

  const params = {
    FunctionName:
      config.GENERATE_RECORD_BRIEFING_GOOGLE_DOC_LINK_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.GENERATE_RECORD_BRIEFING_GOOGLE_DOC_LINK_LAMBDA_FUNCTION,
    );
  }
  return deserializeLambdaPayload<GenerateRecordBriefingGoogleDocLinkResponse>(
    res,
    config.GENERATE_RECORD_BRIEFING_GOOGLE_DOC_LINK_LAMBDA_FUNCTION,
  );
};

export const generateNoticeLetter = async (
  lambdaClient: AWS.Lambda,
  noticeType:
    | "LATE_CANCELLATION"
    | "NO_SHOW"
    | "NO_INTERPRETER"
    | "LATE_RESCHEDULING",
  patientName: string,
  claimNumber: string,
  appointmentDateISO: string,
  appointmentType:
    | AppointmentType.QME
    | AppointmentType.AME
    | AppointmentType.QME_REEVALUATION
    | AppointmentType.AME_REEVALUATION
    | AppointmentType.SIBTF
    | AppointmentType.SIBTF_REEVALUATION
    | AppointmentType.IME
    | AppointmentType.IME_REEVALUATION
    | AppointmentType.IME_ADR
    | AppointmentType.IME_ADR_REEVALUATION,
  doctorID: string,
  adjusterInfo: WorkersCompInsuranceInfo,
  defenseAttorneyInfo: AttorneyInfo | null,
  applicantAttorneyInfo: AttorneyInfo | null,
  signatureDate: string,
  noticeDate: string,
  medicalRecordsPages: number,
  cancellationDate: string | null,
  reschedulingDate: string | null,
  // Backs the declaration's signature location, read from the exam Location.
  appointmentId: string,
): Promise<GenerateNoticeLetterResponse> => {
  const payload_data: GenerateNoticeLetterEvent = {
    notice_type: noticeType,
    appointment_id: appointmentId,
    patient_name: patientName,
    claim_number: claimNumber,
    appointment_date: appointmentDateISO,
    appointment_type: appointmentType,
    doctor_id: doctorID,
    adjuster_info: {
      name: adjusterInfo.adjuster || "",
      insurance_carrier: adjusterInfo.insuranceCarrier || "",
      address: {
        street: adjusterInfo.contact?.address?.street || "",
        city: adjusterInfo.contact?.address?.city || "",
        state: adjusterInfo.contact?.address?.state || "",
        zip_code: adjusterInfo.contact?.address?.zip || "",
      },
    },
    defense_attorney_info: defenseAttorneyInfo
      ? {
          name: defenseAttorneyInfo.name || "",
          law_firm_name: defenseAttorneyInfo.lawFirmName || "",
          address: {
            street: defenseAttorneyInfo.contact?.address?.street || "",
            city: defenseAttorneyInfo.contact?.address?.city || "",
            state: defenseAttorneyInfo.contact?.address?.state || "",
            zip_code: defenseAttorneyInfo.contact?.address?.zip || "",
          },
        }
      : null,
    applicant_attorney_info: applicantAttorneyInfo
      ? {
          name: applicantAttorneyInfo.name || "",
          law_firm_name: applicantAttorneyInfo.lawFirmName || "",
          address: {
            street: applicantAttorneyInfo.contact?.address?.street || "",
            city: applicantAttorneyInfo.contact?.address?.city || "",
            state: applicantAttorneyInfo.contact?.address?.state || "",
            zip_code: applicantAttorneyInfo.contact?.address?.zip || "",
          },
        }
      : null,
    signature_date: signatureDate,
    notice_date: noticeDate,
    medical_records_pages: medicalRecordsPages,
    cancellation_date: cancellationDate,
    rescheduling_date: reschedulingDate,
  };
  const params = {
    FunctionName: config.NOTICE_LETTER_GENERATION_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.NOTICE_LETTER_GENERATION_LAMBDA_FUNCTION,
    );
  }

  const payload = deserializeLambdaPayload<GenerateNoticeLetterResponse>(
    res,
    config.NOTICE_LETTER_GENERATION_LAMBDA_FUNCTION,
  );
  return payload.body;
};

export const generateNotificationForm = async (
  lambdaClient: AWS.Lambda,
  data: GenerateNotificationFormEvent,
): Promise<string> => {
  const params = {
    FunctionName: config.NOTIFICATION_FORM_GENERATION_LAMBDA_FUNCTION,
    Payload: JSON.stringify(data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.NOTIFICATION_FORM_GENERATION_LAMBDA_FUNCTION,
    );
  }

  const payload = deserializeLambdaPayload<GenerateNotificationFormResponse>(
    res,
    config.NOTIFICATION_FORM_GENERATION_LAMBDA_FUNCTION,
  );
  return payload.body.notification_form_pdf;
};

export const generateRecap = async (
  lambdaClient: AWS.Lambda,
  appointmentID: string,
  causation: boolean,
  diagnosis: boolean,
  mmiAndFutureMedicalTreatment: boolean,
  impairmentRating: boolean,
  apportionment: boolean,
  workStatus: boolean,
  recapType: RecapType = "FULL",
) => {
  const selectedSections: SelectedSections = [];

  if (causation) selectedSections.push(RECAP_SECTION_KEYS.CAUSATION);
  if (diagnosis) selectedSections.push(RECAP_SECTION_KEYS.DIAGNOSIS);
  if (mmiAndFutureMedicalTreatment)
    selectedSections.push(RECAP_SECTION_KEYS.MMI_FUTURE_MEDICAL_TREATMENT);
  if (impairmentRating)
    selectedSections.push(RECAP_SECTION_KEYS.IMPAIRMENT_RATING);
  if (apportionment) selectedSections.push(RECAP_SECTION_KEYS.APPORTIONMENT);
  if (workStatus) selectedSections.push(RECAP_SECTION_KEYS.WORK_STATUS);

  const payload_data: GenerateRecapEvent = {
    appointment_record_id: appointmentID,
    recap_type: recapType,
    selected_sections: selectedSections,
  };

  const params = {
    FunctionName: config.GENERATE_RECAP_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(error, config.GENERATE_RECAP_LAMBDA_FUNCTION);
  }
  return deserializeLambdaPayload<GenerateRecapResponse>(
    res,
    config.GENERATE_RECAP_LAMBDA_FUNCTION,
  );
};
export const generatePdfPackageFunction = async (
  lambdaClient: AWS.Lambda,
  appointmentID: string,
  uploadedFileIds: string[],
  includeCurrentReport?: boolean,
) => {
  const payload_data: GeneratePDFPackageRequest = {
    appointment_id: appointmentID,
    uploaded_file_ids: uploadedFileIds,
    ...(includeCurrentReport !== undefined && {
      include_current_report: includeCurrentReport,
    }),
  };

  const params = {
    FunctionName: config.GENERATE_PDF_PACKAGE_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
    console.log("PDF package generated successfully:", res);
  } catch (error) {
    handleLambdaServerError(error, config.GENERATE_PDF_PACKAGE_FUNCTION);
  }
  return deserializeLambdaPayload<GeneratePDFPackageResponse>(
    res,
    config.GENERATE_PDF_PACKAGE_FUNCTION,
  );
};

/**
 * Appends additional body parts using HOI Lambda.
 * @param lambdaClient AWS Lambda client
 * @param appointment_record_id Appointment record ID
 * @param additional_body_parts Array of BodyPart
 * @returns HoiAppendBodyPartsResponse
 */
export const hoiAppendBodyPartsFunction = async (
  lambdaClient: AWS.Lambda,
  appointment_record_id: string,
  additional_body_parts: BodyPart[],
): Promise<HoiAppendBodyPartsResponse> => {
  const payload_data: HoiAppendBodyPartsRequest = {
    appointment_record_id,
    additional_body_parts,
  };
  const params = {
    FunctionName: config.HOI_APPEND_BODY_PARTS_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.HOI_APPEND_BODY_PARTS_LAMBDA_FUNCTION,
    );
  }
  return deserializeLambdaPayload<HoiAppendBodyPartsResponse>(
    res,
    config.HOI_APPEND_BODY_PARTS_LAMBDA_FUNCTION,
  ).body;
};

/**
 * Invokes the GenerateReportEmailFunction Lambda to draft report emails with PDF package attachments for all relevant recipients.
 *
 * @param lambdaClient AWS Lambda client
 * @param appointmentId Appointment ID for which to draft report emails
 * @param drafterName Name of the drafter (e.g., medical assistant)
 * @returns GenerateReportEmailResponse with a list of created Gmail draft IDs
 * @throws LambdaError if the Lambda invocation fails or returns an error
 */
export const generateReportEmailFunction = async (
  lambdaClient: AWS.Lambda,
  appointmentId: string,
  drafterName: string,
) => {
  const payload = {
    appointment_id: appointmentId,
    drafter_name: drafterName,
  };
  const params = {
    FunctionName: config.GENERATE_REPORT_EMAIL_FUNCTION,
    Payload: JSON.stringify(payload),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
    console.log("Report email drafts generated successfully:", res);
  } catch (error) {
    handleLambdaServerError(error, config.GENERATE_REPORT_EMAIL_FUNCTION);
  }
  const deserialized = deserializeLambdaPayload(
    res,
    config.GENERATE_REPORT_EMAIL_FUNCTION,
  );
  return deserialized.body;
};

/**
 * Invokes the GenerateDiagnosticStudyPrescription Lambda to create diagnostic study prescription PDFs.
 *
 * @param lambdaClient AWS Lambda client
 * @param appointmentId The appointment ID for which to generate the prescription
 * @param diagnosis The diagnosis description
 * @param reasonForTesting The reason for requesting the diagnostic test
 * @param bodyPart The body part to be examined
 * @param requestedTest The type of test requested (EMG/NCV, MRI, CT Scan, X-RAY, or free text)
 * @param withContrast Optional - whether to use contrast (for MRI/CT Scan)
 * @returns GenerateDiagnosticStudyPrescriptionResponse with uploaded file ID
 * @throws LambdaError if the Lambda invocation fails or returns an error
 */
export const generateDiagnosticStudyPrescription = async (
  lambdaClient: AWS.Lambda,
  appointmentId: string,
  diagnosis: string,
  reasonForTesting: string,
  bodyPart: string,
  requestedTest: string,
  withContrast?: boolean,
): Promise<GenerateDiagnosticStudyPrescriptionResponse> => {
  const config = getConfig();

  const payload: GenerateDiagnosticStudyPrescriptionEvent = {
    appointment_id: appointmentId,
    diagnosis,
    reason_for_testing: reasonForTesting,
    body_part: bodyPart,
    requested_test: requestedTest,
    ...(withContrast !== undefined && { with_contrast: withContrast }),
  };

  const params = {
    FunctionName: config.DIAGNOSTIC_STUDY_PRESCRIPTION_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.DIAGNOSTIC_STUDY_PRESCRIPTION_LAMBDA_FUNCTION,
    );
  }

  return deserializeLambdaPayload<GenerateDiagnosticStudyPrescriptionResponse>(
    res,
    config.DIAGNOSTIC_STUDY_PRESCRIPTION_LAMBDA_FUNCTION,
  ).body;
};

/**
 * Invokes the AutoGenDiagnosticPrescriptions Lambda to extract diagnostic studies
 * from a published report's assessment section and generate one prescription PDF
 * per study.
 */
export const autoGenerateDiagnosticPrescriptions = async (
  lambdaClient: AWS.Lambda,
  appointmentId: string,
): Promise<{ message: string; generated: number }> => {
  const config = getConfig();

  const payload = {
    appointment_record_id: appointmentId,
  };

  const params = {
    FunctionName: config.AUTO_GENERATE_DIAGNOSTIC_PRESCRIPTIONS_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.AUTO_GENERATE_DIAGNOSTIC_PRESCRIPTIONS_LAMBDA_FUNCTION,
    );
  }

  return deserializeLambdaPayload<{ message: string; generated: number }>(
    res,
    config.AUTO_GENERATE_DIAGNOSTIC_PRESCRIPTIONS_LAMBDA_FUNCTION,
  ).body;
};

/**
 * Generates a Return to Work (RTW) voucher form for a patient appointment.
 * This creates a PDF document indicating whether a patient can return to work
 * following their medical evaluation.
 *
 * @param lambdaClient - AWS Lambda client instance
 * @param appointmentId - The ID of the appointment
 * @param canPatientReturnToWork - Whether the patient can return to work
 * @param cannotReturnToWorkReason - Optional reason why patient cannot return to work
 * @returns GenerateRtwVoucherFormResponse with uploaded file ID
 * @throws LambdaError if the Lambda invocation fails or returns an error
 */
export const generateRtwVoucherForm = async (
  lambdaClient: AWS.Lambda,
  appointmentId: string,
  canPatientReturnToWork: boolean,
  cannotReturnToWorkReason?: string,
): Promise<GenerateRtwVoucherFormResponse> => {
  const config = getConfig();

  const payload: GenerateRtwVoucherFormEvent = {
    appointment_id: appointmentId,
    can_patient_return_to_work: canPatientReturnToWork,
    cannot_return_to_work_reason: cannotReturnToWorkReason || null,
    is_insurance_address_management_enabled: true,
  };

  const params = {
    FunctionName: config.RTW_VOUCHER_FORM_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(error, config.RTW_VOUCHER_FORM_LAMBDA_FUNCTION);
  }

  return deserializeLambdaPayload<GenerateRtwVoucherFormResponse>(
    res,
    config.RTW_VOUCHER_FORM_LAMBDA_FUNCTION,
  ).body;
};

/**
 * Generates a diagnostic test result form and uploads it to S3.
 *
 * @param lambdaClient AWS Lambda client
 * @param appointmentId The appointment ID
 * @param testType The type of test (X-ray, MRI, CT Scan, EMG/NCV)
 * @param bodyPart The body part tested
 * @param viewsType Optional - the type of views taken
 * @param impression The physician's impression/findings
 * @returns GenerateDiagnosticTestResponse with uploaded file ID
 * @throws LambdaError if the Lambda invocation fails or returns an error
 */
export const generateDiagnosticTest = async (
  lambdaClient: AWS.Lambda,
  appointmentId: string,
  testType: string,
  bodyPart: string,
  viewsType: string | null,
  impression: string,
): Promise<GenerateDiagnosticTestResponse> => {
  const config = getConfig();

  const payload: GenerateDiagnosticTestEvent = {
    appointment_id: appointmentId,
    test_type: testType,
    body_part: bodyPart,
    views_type: viewsType,
    impression,
  };

  const params = {
    FunctionName: config.DIAGNOSTIC_TEST_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(error, config.DIAGNOSTIC_TEST_LAMBDA_FUNCTION);
  }

  return deserializeLambdaPayload<GenerateDiagnosticTestResponse>(
    res,
    config.DIAGNOSTIC_TEST_LAMBDA_FUNCTION,
  ).body;
};

/**
 * Generates a Google Doc link for the diagnostic study prescription template.
 * This returns a blank template form that can be used for diagnostic prescriptions.
 *
 * @param lambdaClient AWS Lambda client
 * @returns GenerateDiagnosticStudyPrescriptionGoogleDocResponse with Google Doc link
 * @throws LambdaError if the Lambda invocation fails or returns an error
 */
export const generateDiagnosticPrescriptionLink = async (
  lambdaClient: AWS.Lambda,
): Promise<GenerateDiagnosticStudyPrescriptionGoogleDocResponse> => {
  const config = getConfig();

  const params = {
    FunctionName: config.GENERATE_DIAGNOSTIC_PRESCRIPTION_LINK_LAMBDA_FUNCTION,
    Payload: JSON.stringify({}), // No input needed for template generation
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.GENERATE_DIAGNOSTIC_PRESCRIPTION_LINK_LAMBDA_FUNCTION,
    );
  }

  return deserializeLambdaPayload<GenerateDiagnosticStudyPrescriptionGoogleDocResponse>(
    res,
    config.GENERATE_DIAGNOSTIC_PRESCRIPTION_LINK_LAMBDA_FUNCTION,
  ).body;
};

/**
 * Generates a Google Doc link for the diagnostic test form template.
 * This returns a blank template form that can be used for diagnostic test results.
 *
 * @param lambdaClient AWS Lambda client
 * @returns GenerateDiagnosticTestFormGoogleDocResponse with Google Doc link
 * @throws LambdaError if the Lambda invocation fails or returns an error
 */
export const generateDiagnosticTestFormGoogleDoc = async (
  lambdaClient: AWS.Lambda,
): Promise<GenerateDiagnosticTestFormGoogleDocResponse> => {
  const params = {
    FunctionName:
      config.GENERATE_DIAGNOSTIC_TEST_FORM_GOOGLE_DOC_LAMBDA_FUNCTION,
    Payload: JSON.stringify({}), // No input needed for template generation
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.GENERATE_DIAGNOSTIC_TEST_FORM_GOOGLE_DOC_LAMBDA_FUNCTION,
    );
  }

  return deserializeLambdaPayload<GenerateDiagnosticTestFormGoogleDocResponse>(
    res,
    config.GENERATE_DIAGNOSTIC_TEST_FORM_GOOGLE_DOC_LAMBDA_FUNCTION,
  ).body;
};

/**
 * Generates a recap for a single cover letter file.
 * Processes the uploaded cover letter file and creates a structured recap
 * containing key information and analysis.
 *
 * @param lambdaClient AWS Lambda client
 * @param uploadedFileId The ID of the uploaded cover letter file to process
 * @returns Promise resolving to the recap ID of the generated recap
 * @throws LambdaError if the Lambda invocation fails or returns an error
 */
export const generateCoverLetterRecap = async (
  lambdaClient: AWS.Lambda,
  uploadedFileId: string,
): Promise<string> => {
  const payload_data: GenerateCoverLetterRecapEvent = {
    uploaded_file_id: uploadedFileId,
  };

  const params = {
    FunctionName: config.GENERATE_COVER_LETTER_RECAP_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.GENERATE_COVER_LETTER_RECAP_LAMBDA_FUNCTION,
    );
  }

  const payload = deserializeLambdaPayload<GenerateCoverLetterRecapResponse>(
    res,
    config.GENERATE_COVER_LETTER_RECAP_LAMBDA_FUNCTION,
  );
  return payload.body.recap_id;
};

/**
 * Merges every cover letter one party sent into a single Introduction narrative.
 *
 * Returns the merged text, a source line naming every letter that fed it, and a
 * per-letter audit trail saying which were kept, collapsed as duplicates, or
 * superseded. Nothing is persisted — the caller decides whether to accept the
 * result into that party's introduction statement.
 *
 * @param lambdaClient AWS Lambda client
 * @param appointmentRecordId The appointment whose cover letters are merged
 * @param provider The party; null selects the unattributed "Other" bucket
 * @returns Promise resolving to the merged narrative and its audit trail
 * @throws LambdaError if the party has fewer than two letters, or on failure
 */
export const consolidateParty = async (
  lambdaClient: AWS.Lambda,
  appointmentRecordId: string,
  provider: ConsolidatePartyEvent["provider"],
): Promise<ConsolidatePartyResponse> => {
  const payload_data: ConsolidatePartyEvent = {
    appointment_record_id: appointmentRecordId,
    provider,
  };

  const params = {
    FunctionName: config.CONSOLIDATE_PARTY_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(error, config.CONSOLIDATE_PARTY_LAMBDA_FUNCTION);
  }

  const payload = deserializeLambdaPayload<ConsolidatePartyResponse>(
    res,
    config.CONSOLIDATE_PARTY_LAMBDA_FUNCTION,
  );
  return payload.body;
};

/**
 * Generates a consolidated intro paragraph from multiple cover letter recaps.
 * Analyzes all provided cover letter files and creates a unified introductory
 * paragraph that summarizes the key findings across all documents.
 *
 * @param lambdaClient AWS Lambda client
 * @param appointmentRecordId The appointment record ID for context
 * @param uploadedFileIds Array of cover letter file IDs to process
 * @returns Promise resolving to the intro paragraph ID
 * @throws LambdaError if the Lambda invocation fails or returns an error
 */
export const generateIntroParagraph = async (
  lambdaClient: AWS.Lambda,
  appointmentRecordId: string,
  uploadedFileIds: string[],
): Promise<string> => {
  const payload_data: GenerateIntroParagraphEvent = {
    appointment_record_id: appointmentRecordId,
    uploaded_file_ids: uploadedFileIds,
  };

  const params = {
    FunctionName: config.GENERATE_INTRO_PARAGRAPH_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.GENERATE_INTRO_PARAGRAPH_LAMBDA_FUNCTION,
    );
  }

  const payload = deserializeLambdaPayload<GenerateIntroParagraphResponse>(
    res,
    config.GENERATE_INTRO_PARAGRAPH_LAMBDA_FUNCTION,
  );
  return payload.body.intro_paragraph_id;
};

/**
 * Generates all cover letter recaps and intro paragraph in a single atomic operation.
 * Processes all cover letter files in parallel to generate individual recaps,
 * then creates a consolidated intro paragraph based on all the recaps.
 *
 * @param lambdaClient AWS Lambda client
 * @param appointmentRecordId The appointment record ID for context
 * @param uploadedFileIds Array of cover letter file IDs to process
 * @returns Promise resolving to response containing intro paragraph ID and all recap IDs
 * @throws LambdaError if the Lambda invocation fails or returns an error
 */
export const generateAllCoverLetter = async (
  lambdaClient: AWS.Lambda,
  appointmentRecordId: string,
  uploadedFileIds: string[],
): Promise<GenerateAllResponse> => {
  const payload_data: GenerateAllEvent = {
    appointment_record_id: appointmentRecordId,
    uploaded_file_ids: uploadedFileIds,
  };

  const params = {
    FunctionName: config.GENERATE_ALL_COVER_LETTER_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.GENERATE_ALL_COVER_LETTER_LAMBDA_FUNCTION,
    );
  }

  return deserializeLambdaPayload<GenerateAllResponse>(
    res,
    config.GENERATE_ALL_COVER_LETTER_LAMBDA_FUNCTION,
  ).body;
};

/**
 * Synchronously run the V2 assessment review generation (review_gen lambda):
 * generates the assessment and writes the ASSESSMENT_REVIEW WorkflowAudit that
 * the assessment review debug page reads. Normally fired async on mark-as-done;
 * this sync path backfills the audit when it doesn't exist yet.
 */
export const runAssessmentReviewGen = async (
  lambdaClient: AWS.Lambda,
  appointmentRecordId: string,
): Promise<AssessmentReviewGenResponse> => {
  const payload_data: AssessmentReviewGenEvent = {
    appointmentRecordId: appointmentRecordId,
  };

  const params = {
    FunctionName: config.ASSESSMENT_REVIEW_GEN_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.ASSESSMENT_REVIEW_GEN_LAMBDA_FUNCTION,
    );
  }

  return deserializeLambdaPayload<AssessmentReviewGenResponse>(
    res,
    config.ASSESSMENT_REVIEW_GEN_LAMBDA_FUNCTION,
  ).body;
};

/**
 * Populate the FREEFORM assessment from the appointment's dictation
 * (dictation_populate lambda): extraction + V2 preview, the same two steps
 * report generation runs.
 *
 * The lambda also runs itself on audio-ingestion completion, so this is the
 * physician asking for it early or again. It takes 1-3 minutes; callers watch
 * the ASSESSMENT_DICTATION_POPULATE OperationStatus row rather than this
 * promise, which is why the invoke is fire-and-forget.
 *
 * `force` says a person asked: it takes over a run already holding the
 * appointment (including one that died and left the row RUNNING) and replaces
 * an assessment even when it has been edited. Confirm with the user first.
 */
export const populateAssessmentFromDictation = async (
  lambdaClient: AWS.Lambda,
  appointmentRecordId: string,
  force = false,
): Promise<void> => {
  const payload_data: AssessmentDictationPopulateEvent = {
    appointmentRecordId: appointmentRecordId,
    force,
  };

  try {
    await lambdaClient
      .invoke({
        FunctionName: getFunctionNameWithAlias(
          config.ASSESSMENT_DICTATION_POPULATE_LAMBDA_FUNCTION,
        ),
        // Event: the run outlives any sensible client wait, and its outcome
        // reaches the portal through the OperationStatus subscription.
        InvocationType: "Event",
        Payload: JSON.stringify(payload_data),
      })
      .promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.ASSESSMENT_DICTATION_POPULATE_LAMBDA_FUNCTION,
    );
  }
};

export const generatePreHistoryTemplate = async (
  lambdaClient: AWS.Lambda,
  appointmentID: string,
) => {
  const payload_data: GeneratePreHistoryTemplateEvent = {
    appointment_record_id: appointmentID,
  };

  const params = {
    FunctionName: config.GENERATE_PRE_HISTORY_TEMPLATE_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.GENERATE_PRE_HISTORY_TEMPLATE_LAMBDA_FUNCTION,
    );
  }
  return deserializeLambdaPayload<GeneratePreHistoryTemplateResponse>(
    res,
    config.GENERATE_PRE_HISTORY_TEMPLATE_LAMBDA_FUNCTION,
  );
};

/**
 * Generates a QME/Workers' Comp style job duties description.
 * Takes employer info and generates a professional job description with physical demands.
 *
 * @deprecated Use generateJobDuties from ~/services/jobDuties instead (behind USE_ECS_JOB_DUTIES flag).
 * @param lambdaClient AWS Lambda client
 * @param employer Employer name
 * @param jobTitle Job title
 * @param hoursPerWeek Hours worked per week (optional)
 * @param existingJobDuties Existing job duties to expand upon (optional)
 * @param jobEndDate Job end date in YYYY-MM-DD format. If provided, prompt uses past tense; if null, present tense.
 * @returns Generated job duties description
 */
export const generateJobDuties = async (
  lambdaClient: AWS.Lambda,
  employer: string,
  jobTitle: string,
  hoursPerWeek: string | null,
  existingJobDuties: string | null,
  jobEndDate: string | null,
): Promise<string> => {
  const payload_data: GenerateJobDutiesEvent = {
    employer,
    job_title: jobTitle,
    hours_per_week: hoursPerWeek,
    existing_job_duties: existingJobDuties,
    job_end_date: jobEndDate,
  };

  const params = {
    FunctionName: config.GENERATE_JOB_DUTIES_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(error, config.GENERATE_JOB_DUTIES_LAMBDA_FUNCTION);
  }

  const payload = deserializeLambdaPayload<GenerateJobDutiesResponse>(
    res,
    config.GENERATE_JOB_DUTIES_LAMBDA_FUNCTION,
  );
  return payload.body.job_duties;
};

/**
 * Formats a raw address string into structured address components.
 * Uses AI to parse and standardize addresses according to USPS standards.
 *
 * @deprecated Use formatAddress from ~/api/apiClient instead (behind USE_ECS_ADDRESS_FORMAT flag).
 */
export const formatAddress = async (
  lambdaClient: AWS.Lambda,
  rawAddress: string,
  entityName: string | null,
): Promise<FormatAddressResponse> => {
  const payload_data: FormatAddressEvent = {
    raw_address: rawAddress,
    entity_name: entityName,
  };

  const params = {
    FunctionName: config.FORMAT_ADDRESS_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(error, config.FORMAT_ADDRESS_LAMBDA_FUNCTION);
  }

  const payload = deserializeLambdaPayload<FormatAddressResponse>(
    res,
    config.FORMAT_ADDRESS_LAMBDA_FUNCTION,
  );
  return payload.body;
};

/**
 * Corrects misspelled medication names in text.
 * Uses AI to identify and fix common medication spelling errors while preserving
 * all other text exactly as-is.
 *
 * @param lambdaClient AWS Lambda client
 * @param medicationText The medication notes text to correct
 * @returns Corrected medication text
 */
export const correctMedications = async (
  lambdaClient: AWS.Lambda,
  medicationText: string,
): Promise<string> => {
  const payload_data: CorrectMedicationsEvent = {
    medication_text: medicationText,
  };

  const params = {
    FunctionName: config.CORRECT_MEDICATIONS_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(error, config.CORRECT_MEDICATIONS_LAMBDA_FUNCTION);
  }

  const payload = deserializeLambdaPayload<CorrectMedicationsResponse>(
    res,
    config.CORRECT_MEDICATIONS_LAMBDA_FUNCTION,
  );
  return payload.body.corrected_text;
};

// ******************************************************
// ************** APPOINTMENT EVENTS ********************
// ******************************************************

/**
 * Emits an appointment event to the event stream via the ECS API server.
 * This triggers downstream workflows via the event-driven architecture.
 *
 * The ECS server derives ``actor_type`` / ``actor_id`` from the request
 * JWT, so no actor is passed from the client. ``_lambdaClient`` is unused
 * (the event is POSTed to ECS, not invoked as a Lambda) but retained as
 * the first parameter because the service wrapper injects the AWS Lambda
 * client positionally for every function in the module.
 *
 * @param appointmentId The appointment ID
 * @param entity The entity type (e.g., APPOINTMENT_HISTORY_OF_INJURY)
 * @param action The action (e.g., PUBLISHED, GENERATED)
 * @returns Empty string — the batch-queue path produces no event_id, and
 *   every call site is fire-and-forget (``void emitAppointmentEvent(...)``).
 */
export const emitAppointmentEvent = async (
  _lambdaClient: AWS.Lambda,
  appointmentId: string,
  entity: AppointmentEventEntity,
  action: AppointmentEventAction,
  entityId?: string | null,
  properties?: Record<string, unknown> | null,
): Promise<string> => {
  await enqueueAppointmentEventViaECS({
    appointmentId,
    entity,
    action,
    entityId,
    properties,
  });
  return "";
};

export const calculateDifficultyLevel = async (
  lambdaClient: AWS.Lambda,
  appointmentRecordId: string,
): Promise<DifficultyLevelResponse> => {
  const config = getConfig();
  const payload: DifficultyLevelEvent = {
    appointment_record_id: appointmentRecordId,
  };

  const params = {
    FunctionName: config.CALCULATE_DIFFICULTY_LEVEL_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.CALCULATE_DIFFICULTY_LEVEL_LAMBDA_FUNCTION,
    );
  }

  return deserializeLambdaPayload<DifficultyLevelResponse>(
    res,
    config.CALCULATE_DIFFICULTY_LEVEL_LAMBDA_FUNCTION,
  ).body;
};

// ******************************************************
// ************** BACKFILL FUNCTIONS ********************
// ******************************************************

/**
 * Invokes the BackfillOnsiteOps Lambda synchronously and returns the result
 * counts (appointments processed / reassigned), so callers can report
 * completion. The lambda reassigns each appointment's on-site MA from the
 * (doctor, location) -> primary-MA mapping, scoped to the given location and
 * floored at startDate.
 *
 * @param lambdaClient AWS Lambda client
 * @param payload doctorID to scope the backfill to, plus an optional locationID
 *   (scope to one location) and startDate (YYYY-MM-DD floor on appointment dates)
 */
export const backfillOnsiteOps = async (
  lambdaClient: AWS.Lambda,
  payload: { doctorID: string; locationID?: string; startDate?: string },
): Promise<DeserializedLambdaSuccessPayload<BackfillOnsiteOpsResponse>> => {
  const params = {
    FunctionName: getFunctionNameWithAlias(
      config.BACKFILL_ONSITE_OPS_LAMBDA_FUNCTION,
    ),
    Payload: JSON.stringify(payload),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(error, config.BACKFILL_ONSITE_OPS_LAMBDA_FUNCTION);
  }

  return deserializeLambdaPayload<BackfillOnsiteOpsResponse>(
    res,
    config.BACKFILL_ONSITE_OPS_LAMBDA_FUNCTION,
  );
};

// ******************************************************
// ************** PRIVATE FUNCTIONS *********************
// ******************************************************

export interface DeserializedLambdaSuccessPayload<T> {
  statusCode: number;
  body: T;
}

/**
 * Extract a human-readable message from a Lambda error body. Handlers return
 * the error body as a JSON-stringified object like `{"message": "..."}`; pull
 * that `message` out. Falls back to the raw body (or undefined) when it isn't
 * the expected shape.
 */
function extractLambdaErrorMessage(body?: string): string | undefined {
  if (!body) return undefined;
  try {
    const parsed = JSON.parse(body) as { message?: unknown };
    if (typeof parsed.message === "string") return parsed.message;
  } catch {
    // Not JSON — fall through to the raw body.
  }
  return body;
}

/**
 * Deserializes a Lambda function response payload and extracts the body content.
 *
 * Lambda functions typically return responses in the format:
 * {
 *   statusCode: number,
 *   body: string (JSON stringified)
 * }
 *
 * This function parses the outer JSON structure and then parses the body field
 * to extract the actual response data. It performs comprehensive error validation
 * including Lambda execution errors, HTTP status codes, and payload validation.
 *
 * @template T - The expected type of the deserialized body content
 * @param response - The full Lambda invocation response object containing the payload and metadata
 * @param functionName - The name of the Lambda function (used for error context and logging)
 * @returns An object containing the statusCode and the deserialized body of type T
 * @throws {LambdaError} If the Lambda execution fails, payload is empty, or contains error status codes
 * @throws {Error} If the payload cannot be parsed as valid JSON
 * @example
 * ```typescript
 * const lambdaResponse = await lambdaClient.invoke(params).promise();
 * const response = deserializeLambdaPayload<MyResponseType>(lambdaResponse, "my-function");
 * // response = { statusCode: 200, body: MyResponseType }
 * ```
 */
export function deserializeLambdaPayload<T>(
  response: PromiseResult<InvocationResponse, AWSError>,
  functionName: string,
): DeserializedLambdaSuccessPayload<T> {
  checkLambdaExecutionError(functionName, response);

  if (!response.Payload) {
    throw new LambdaError(functionName, "Empty payload", undefined, {
      requestId: response.$response.requestId,
    });
  }

  const parsed = JSON.parse(response.Payload.toString()) as {
    statusCode: number;
    body?: string;
  };

  if (parsed.statusCode >= 400) {
    // Handlers return the error body as JSON-stringified `{"message": ...}`.
    // Pull the human-readable message out so callers can surface the real,
    // actionable reason (e.g. "Doctor has no phone number") instead of a
    // generic failure. Stashed structured in `extra.serverMessage`.
    const serverMessage = extractLambdaErrorMessage(parsed.body);
    throw new LambdaError(
      functionName,
      `returned status code ${parsed.statusCode}: ${serverMessage ?? "No message"}`,
      undefined,
      {
        requestId: response.$response.requestId,
        statusCode: parsed.statusCode,
        serverMessage,
      },
    );
  }

  // Handle case where payload body is empty
  if (parsed.body === undefined || parsed.body === null || parsed.body === "") {
    return {
      statusCode: parsed.statusCode,
      body: undefined as T,
    };
  }

  const body = JSON.parse(parsed.body) as T;

  return {
    ...parsed,
    body,
  };
}

export const getJotformSubmission = async (
  lambdaClient: AWS.Lambda,
  appointmentID: string,
  language?: Language,
  formType?: JotformFormType,
): Promise<GetJotformSubmissionResponse> => {
  const config = getConfig();

  const payload_data: GetJotformSubmissionByAppointmentEvent = {
    appointmentID,
    ...(language && { language }),
    ...(formType && { formType }),
  };

  const params = {
    FunctionName: config.JOTFORM_GET_SUBMISSION_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.JOTFORM_GET_SUBMISSION_LAMBDA_FUNCTION,
    );
  }

  return deserializeLambdaPayload<GetJotformSubmissionResponse>(
    res,
    config.JOTFORM_GET_SUBMISSION_LAMBDA_FUNCTION,
  ).body;
};

export const getJotformSubmissionById = async (
  lambdaClient: AWS.Lambda,
  submissionID: string,
): Promise<GetJotformSubmissionResponse> => {
  const config = getConfig();

  const payload_data: GetJotformSubmissionByIdEvent = {
    submissionID,
  };

  const params = {
    FunctionName: config.JOTFORM_GET_SUBMISSION_LAMBDA_FUNCTION,
    Payload: JSON.stringify(payload_data),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.JOTFORM_GET_SUBMISSION_LAMBDA_FUNCTION,
    );
  }

  return deserializeLambdaPayload<GetJotformSubmissionResponse>(
    res,
    config.JOTFORM_GET_SUBMISSION_LAMBDA_FUNCTION,
  ).body;
};

function handleLambdaServerError(error: unknown, functionName: string): never {
  if (isClockSkewError(error)) {
    throw new ClockSkewError(error);
  }
  if (error instanceof Error) {
    switch (error.name) {
      case "NetworkingError":
        throw new NetworkError(`Network issue: ${error.message}`);

      // The browser cancelled the request (navigation, reload, tab close, or
      // an unmount mid-invoke). The lambda itself never reported a failure, so
      // don't dress this up as one — see AWSRequestAbortedError.
      case "RequestAbortedError":
        throw new AWSRequestAbortedError(functionName, error);

      case "AccessDeniedException":
        throw new AWSAuthError(error);

      case "ValidationException":
        throw new LambdaError(functionName, "Invalid input", error);

      case "ResourceNotFoundException":
        throw new LambdaError(functionName, "Resource not found", error);

      case "ThrottlingException":
        throw new LambdaError(functionName, "Too many requests", error);

      case "InternalServerError":
      case "ServiceException":
        throw new LambdaError(functionName, "AWS internal error", error);

      case "TimeoutError":
        throw new LambdaError(functionName, "Request timed out", error);

      case "UnauthorizedException":
      case "UnrecognizedClientException":
      case "ExpiredTokenException":
      case "InvalidIdentityTokenException":
        throw new AWSAuthError(error);

      case "RequestEntityTooLargeException":
        throw new LambdaError(functionName, "Payload too large", error);

      case "InvalidParameterException":
        throw new LambdaError(functionName, "Invalid parameter", error);
    }
    // Fallthrough for other error types that are still an `Error` instance.
    throw new LambdaError(functionName, "Unknown error", error);
  }

  // Handle cases where the error is not an `Error` instance.
  throw new LambdaError(
    functionName,
    `Unknown error occurred: ${JSON.stringify(error)}`,
  );
}

// This function handles any exception raised by the lambda function
function checkLambdaExecutionError(
  functionName: string,
  res: PromiseResult<InvocationResponse, AWSError>,
) {
  // Error in this format:
  // {
  //   FunctionError: "Unhandled";
  //   Payload: '{"errorMessage": ..., "errorType": ..., "requestId": ..., "stackTrace": ...}';
  //   StatusCode: 200;
  // }
  if (res.FunctionError) {
    let errDetails: Record<string, unknown>;
    try {
      errDetails = JSON.parse(res.Payload?.toString() ?? "{}") as Record<
        string,
        unknown
      >;
    } catch {
      errDetails = {
        error: res.Payload?.toString(),
        requestId: res.$response.requestId,
      };
    }
    throw new LambdaError(
      functionName,
      `Error within lambda execution: ${res.FunctionError}`,
      undefined,
      errDetails,
    );
  }

  // Note (Sompop): unsure of error format in this case, so just going to pass the whole payload as string to the error
  if (res.StatusCode && res.StatusCode >= 400) {
    throw new LambdaError(
      functionName,
      `Error status: ${res.StatusCode} - ${res.Payload?.toString() || "No payload"}`,
      undefined,
      {
        statusCode: res.StatusCode,
        requestId: res.$response.requestId,
      },
    );
  }
}

export interface VectorSearchDebugRequest {
  query: string;
  m?: number;
  top_n?: number;
  reranker?: string;
  appointment_id?: string;
  rrf_k_knn?: number;
  rrf_k_bm25_default?: number;
  rrf_k_bm25_boosted?: number;
  rrf_k_rerank?: number;
  keyword_only?: boolean;
  start_page?: number;
  end_page?: number;
  filename?: string;
}

export const vectorSearchDebug = async (
  lambdaClient: AWS.Lambda,
  request: VectorSearchDebugRequest,
) => {
  const config = getConfig();

  const params = {
    FunctionName: getFunctionNameWithAlias(
      config.VECTOR_SEARCH_DEBUG_LAMBDA_FUNCTION,
    ),
    Payload: JSON.stringify(request),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(error, config.VECTOR_SEARCH_DEBUG_LAMBDA_FUNCTION);
  }

  return deserializeLambdaPayload<Record<string, unknown>>(
    res,
    config.VECTOR_SEARCH_DEBUG_LAMBDA_FUNCTION,
  ).body;
};

/**
 * Builds a dummy appointment (dev/stage only) via the same
 * IntegrationTestDataManager the integration suites use. Takes ~40s with
 * everything enabled, so callers should invoke it with a generous timeout.
 */
export const createTestAppointment = async (
  lambdaClient: AWS.Lambda,
  request: CreateTestAppointmentEvent,
): Promise<CreateTestAppointmentResponse> => {
  const config = getConfig();
  const functionName = config.CREATE_TEST_APPOINTMENT_LAMBDA_FUNCTION;
  if (!functionName) {
    throw new LambdaError(
      "Test appointment creation is only available on dev and stage.",
    );
  }

  const params = {
    FunctionName: functionName,
    Payload: JSON.stringify(request),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(error, functionName);
  }

  return deserializeLambdaPayload<CreateTestAppointmentResponse>(
    res,
    functionName,
  ).body;
};

export const initiateImpersonation = async (
  lambdaClient: AWS.Lambda,
  targetId: string,
  targetType: "DOCTOR" | "MEDICAL_ASSISTANT",
  reason: string,
  idToken: string,
): Promise<InitiateImpersonationResponse> => {
  const config = getConfig();

  const bodyPayload: Record<string, string> = {
    action: "initiate",
    reason,
  };
  if (targetType === "DOCTOR") {
    bodyPayload.physician_id = targetId;
  } else {
    bodyPayload.medical_assistant_id = targetId;
  }

  const params = {
    FunctionName: config.IMPERSONATION_LAMBDA_FUNCTION,
    Payload: JSON.stringify({
      headers: { Authorization: `Bearer ${idToken}` },
      body: JSON.stringify(bodyPayload),
    }),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(error, config.IMPERSONATION_LAMBDA_FUNCTION);
  }

  return deserializeLambdaPayload<InitiateImpersonationResponse>(
    res,
    config.IMPERSONATION_LAMBDA_FUNCTION,
  ).body;
};

export const endImpersonation = async (
  lambdaClient: AWS.Lambda,
  sessionId: string,
  idToken: string,
): Promise<EndImpersonationResponse> => {
  const config = getConfig();

  const params = {
    FunctionName: config.IMPERSONATION_LAMBDA_FUNCTION,
    Payload: JSON.stringify({
      headers: { Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({
        action: "end",
        session_id: sessionId,
      }),
    }),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(error, config.IMPERSONATION_LAMBDA_FUNCTION);
  }

  return deserializeLambdaPayload<EndImpersonationResponse>(
    res,
    config.IMPERSONATION_LAMBDA_FUNCTION,
  ).body;
};

/**
 * Splits the source PDF for a REVIEW_ATTACHMENT_CLASSIFICATION action and
 * uploads one labeled UploadedFile per section. The portal marks the action
 * RESOLVED separately via useResolveWorkflowAuditActions.
 */
export const resolveAttachmentReview = async (
  lambdaClient: AWS.Lambda,
  event: ResolveAttachmentReviewEvent,
): Promise<ResolveAttachmentReviewResponse> => {
  const params = {
    FunctionName: config.RESOLVE_ATTACHMENT_REVIEW_LAMBDA_FUNCTION,
    Payload: JSON.stringify(event),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(
      error,
      config.RESOLVE_ATTACHMENT_REVIEW_LAMBDA_FUNCTION,
    );
  }

  return deserializeLambdaPayload<ResolveAttachmentReviewResponse>(
    res,
    config.RESOLVE_ATTACHMENT_REVIEW_LAMBDA_FUNCTION,
  ).body;
};

/**
 * Merges several existing UploadedFiles into a single stitched PDF, creates one
 * new UploadedFile, deletes the originals, and writes a MANUAL_FILE_MERGE audit
 * entry. Invoked synchronously so the caller gets the merged file id back.
 */
export const mergeUploadedFiles = async (
  lambdaClient: AWS.Lambda,
  event: MergeUploadedFilesEvent,
): Promise<MergeUploadedFilesResponse> => {
  const params = {
    FunctionName: config.MERGE_UPLOADED_FILES_LAMBDA_FUNCTION,
    Payload: JSON.stringify(event),
  };

  let res;
  try {
    res = await lambdaClient.invoke(params).promise();
  } catch (error) {
    handleLambdaServerError(error, config.MERGE_UPLOADED_FILES_LAMBDA_FUNCTION);
  }

  return deserializeLambdaPayload<MergeUploadedFilesResponse>(
    res,
    config.MERGE_UPLOADED_FILES_LAMBDA_FUNCTION,
  ).body;
};
